import { RedoMerchantRpcClient } from "@redotech/merchant-sdk/rpc/client";
import {
  GlobalSearchHistoryItemType,
  GlobalSearchItem,
} from "@redotech/merchant-sdk/rpc/schema/global-search/global-search-schema";
import {
  MerchantRoutes,
  useMerchantRoutes,
} from "@redotech/redo-merchant-app-common/merchant-routes";
import { getOrderNavigationUrl } from "@redotech/redo-merchant-fulfillment-app/common/order-navigation-url";
import { GlobalSearchCategory } from "@redotech/redo-views-common/global-search/global-search-category";
import {
  CommandPaletteItem,
  SearchParams,
} from "@redotech/redo-web/command-palette/contexts/command-palette-context";
import { filterTruthy } from "@redotech/util/array";
import { useCallback } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { shipmentStatusName } from "../../../return/util";
import { usePageSearch } from "./global-search-tools/use-page-search";
import { useSettingsItems } from "./settings";
import {
  removeSettingsNavigationDuplicatePageResults,
  searchSettingsNavigationItems,
} from "./settings-navigation-search";

export function useSearchItems(merchantRpcClient: RedoMerchantRpcClient) {
  const routes = useMerchantRoutes();
  const navigate = useNavigate();
  const searchPages = usePageSearch();
  const settingsItems = useSettingsItems();

  return useCallback(
    async ({
      query,
      filters,
      signal,
    }: SearchParams): Promise<CommandPaletteItem[]> => {
      if (!query.trim()) {
        return [];
      }

      // Determine category filter
      const category =
        filters.length === 1 ? (filters[0] as GlobalSearchCategory) : undefined;

      // Search pages client-side
      const shouldSearchPages = !category || category === "pages";
      const settingsNavigationResults = shouldSearchPages
        ? searchSettingsNavigationItems({
            query,
            routes,
            sections: settingsItems ?? [],
            navigate,
            limit: category === "pages" ? 10 : 3,
            viewerVisibility: "merchant",
          })
        : [];
      const pageResults = shouldSearchPages
        ? searchPages(query, category === "pages" ? 10 : 3)
        : [];
      const dedupedPageResults = shouldSearchPages
        ? removeSettingsNavigationDuplicatePageResults({
            pageResults,
            settingsResults: settingsNavigationResults,
          })
        : [];

      // If only searching pages, return early
      if (category === "pages") {
        return [...settingsNavigationResults, ...dedupedPageResults].slice(
          0,
          10,
        );
      }

      // Fetch global search results from backend
      let globalSearchResults: CommandPaletteItem[] = [];
      try {
        const response = await merchantRpcClient.getGlobalSearchResults(
          { searchText: query, category },
          { signal },
        );

        globalSearchResults = response.results.map((item: GlobalSearchItem) =>
          globalSearchItemToCommandPaletteItem(item, routes, navigate),
        );
      } catch (error: unknown) {
        // Re-throw abort errors so caller can handle them appropriately
        if (error instanceof Error && error.name === "AbortError") {
          throw error;
        }
        console.warn("Failed to fetch global search results:", error);
      }

      // Combine results: pages + backend results
      return [
        ...settingsNavigationResults,
        ...dedupedPageResults,
        ...globalSearchResults,
      ];
    },
    [merchantRpcClient, routes, navigate, searchPages, settingsItems],
  );
}

function globalSearchItemToCommandPaletteItem(
  result: GlobalSearchItem,
  routes: MerchantRoutes,
  navigate: NavigateFunction,
): CommandPaletteItem {
  switch (result.type) {
    case GlobalSearchHistoryItemType.ORDER:
      return {
        id: `order-${result.id}`,
        title: result.orderName,
        subtitle: filterTruthy([
          result.customerName,
          result.orderCreatedAt.toLocaleDateString(),
          `$${result.price.toFixed(2)}`,
          `${result.items} items`,
          shipmentStatusName(result.fulfillmentStatus),
        ]).join(" | "),
        category: "orders",
        group: "Orders",
        action: ({ newTab }) => {
          const href = `${routes.shippingBaseUrl}/outbound-labels/orders/${result.id}`;
          if (newTab) {
            window.open(href, "_blank");
          } else {
            navigate(href);
          }
        },
      };

    case GlobalSearchHistoryItemType.SHIPMENT:
      return {
        id: `shipment-${result.id}`,
        title: result.orderName,
        subtitle: filterTruthy([
          result.recipientName,
          result.shipmentCreatedAt.toLocaleDateString(),
          result.trackingCode,
          shipmentStatusName(result.shipmentStatus),
        ]).join(" | "),
        category: "shipments",
        group: "Fulfillments",
        action: ({ newTab }) => {
          const href = getOrderNavigationUrl({
            fulfillmentGroupId: result.id,
            orderId: { id: result.externalOrderId },
            ordersOutboundLabelsHref: routes.ordersOutboundLabelsHref,
          });
          if (newTab) {
            window.open(href, "_blank");
          } else {
            navigate(href);
          }
        },
      };

    case GlobalSearchHistoryItemType.CUSTOMER:
      return {
        id: `customer-${result.id}`,
        title: result.customerName,
        subtitle: filterTruthy([result.email, result.phone]).join(" | "),
        category: "customers",
        group: "Customers",
        action: ({ newTab }) => {
          const href = routes.getCustomerRecordHref({ customerId: result.id });
          if (newTab) {
            window.open(href, "_blank");
          } else {
            navigate(href);
          }
        },
      };

    case GlobalSearchHistoryItemType.RETURN:
      return {
        id: `return-${result.id}`,
        title: result.orderName,
        subtitle: filterTruthy([
          result.customerName,
          result.returnStartedAtDate.toLocaleDateString(),
          result.returnValue != null
            ? `$${result.returnValue.toFixed(2)}`
            : null,
          result.returnType,
          shipmentStatusName(result.returnStatus),
        ]).join(" | "),
        category: "returns",
        group: "Returns",
        action: ({ newTab }) => {
          const href = `${routes.returnsHref}/all-returns/${result.id}`;
          if (newTab) {
            window.open(href, "_blank");
          } else {
            navigate(href);
          }
        },
      };

    case GlobalSearchHistoryItemType.CONVERSATION:
      return {
        id: `conversation-${result.id}`,
        title: result.customer,
        subtitle: filterTruthy([
          result.createdAt.toLocaleDateString(),
          result.subject,
          result.platform,
        ]).join(" | "),
        category: "conversations",
        group: "Conversations",
        action: ({ newTab }) => {
          const href = routes.getConversationHref(result.id);
          if (newTab) {
            window.open(href, "_blank");
          } else {
            navigate(href);
          }
        },
      };

    case GlobalSearchHistoryItemType.DOCUMENTATION:
      return {
        id: `article-${result.id}`,
        title: result.title,
        subtitle: result.description || undefined,
        category: "help-articles",
        group: "Help Articles",
        action: () => {
          window.open(result.url, "_blank");
        },
      };

    case GlobalSearchHistoryItemType.PAGE:
      return {
        id: `page-${result.id}`,
        title: result.breadcrumbs[result.breadcrumbs.length - 1] || "Page",
        subtitle: result.breadcrumbs.slice(0, -1).join(" > ") || undefined,
        category: "pages",
        group: "Pages",
        action: ({ newTab }) => {
          // Page id is the route path
          if (newTab) {
            window.open(result.id, "_blank");
          } else {
            navigate(result.id);
          }
        },
      };

    default: {
      // Type guard to ensure exhaustive check
      const _exhaustiveCheck: never = result;
      throw new Error(`Unhandled GlobalSearchItem type: ${_exhaustiveCheck}`);
    }
  }
}
